import React, { useEffect, useState } from "react";
import { Chart } from "react-google-charts";
import axios from "axios";
import { StandardURL } from "../App";

const MoneyStatistics = ({ propertyId }) => {
  const [monthlyData, setMonthlyData] = useState(null);
  const [annualData, setAnnualData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [monthlyResponse, annualResponse] = await Promise.all([
          axios.get(
            `${StandardURL}/iNyumba/user/moneystatisticslandlord/viewmonths/${propertyId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            `${StandardURL}/iNyumba/user/moneystatisticslandlord/viewannual/${propertyId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        setMonthlyData(monthlyResponse.data?.response || null);
        setAnnualData(annualResponse.data?.response || null);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId, token]); // Added token to dependency array for useEffect

  const formatChartData = (data) => {
    if (!data) return [];

    // Ensure numeric types and handle potential NaN values
    const actualAmount = parseFloat(data.actualamountminuscommision) || 0;
    const commission = parseFloat(data.commission) || 0;
    const settlementAmount = parseFloat(data.settlementAmount) || 0;
    const transactionCost = parseFloat(data.transactionCost) || 0;
    const monthlySales = parseFloat(data.monthlySales) || 0; // Added monthlySales for the table if needed, though not directly in chart data calculation

    const total = actualAmount + commission + settlementAmount + transactionCost;

    if (total === 0) return [];

    return [
      ["Category", "Amount"],
      ["Net Profit", actualAmount],
      ["Commission", commission],
      ["Withdrawals", settlementAmount],
      ["Transaction Cost", transactionCost],
    ];
  };

  const renderChart = (data) => (
    <Chart
      chartType="PieChart"
      width="100%"
      height="300px"
      data={formatChartData(data)}
      options={{
        pieHole: 0.4,
        is3D: false,
        backgroundColor: "transparent",
        chartArea: { width: "90%", height: "80%" },
        legend: { position: "bottom", textStyle: { color: "#374151" } }, // Tailwind gray-700 equivalent
        colors: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B'], // Example Tailwind-inspired colors: Blue, Red, Green, Yellow
        tooltip: { textStyle: { color: '#1F2937' } } // Tailwind gray-900 equivalent
      }}
    />
  );

  const renderTable = (data) => (
    <table className="w-full border-collapse mt-4 text-sm md:text-base">
      <thead className="bg-gray-100">
        <tr>
          <th className="p-2 md:p-3 text-left border-b-2 border-gray-300 text-gray-700 font-semibold">Category</th>
          <th className="p-2 md:p-3 text-left border-b-2 border-gray-300 text-gray-700 font-semibold">Amount (Ksh)</th>
        </tr>
      </thead>
      <tbody>
        {data &&
          formatChartData(data)
            .slice(1) // Skip the header row from formatChartData
            .map(([category, amount], index) => (
              <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                <td className="p-2 md:p-3 border-b border-gray-200 text-gray-800">{category}</td>
                <td className="p-2 md:p-3 border-b border-gray-200 text-gray-900 font-medium">{amount.toLocaleString()}</td>
              </tr>
            ))}
        {/* You can also add total rows if your data includes them, e.g.: */}
        {data && (
            <tr className="bg-blue-50 font-bold">
                <td className="p-2 md:p-3 border-b border-blue-200 text-blue-800">Total Revenue</td>
                <td className="p-2 md:p-3 border-b border-blue-200 text-blue-900">
                    {(parseFloat(data.actualamountminuscommision || 0) + parseFloat(data.commission || 0)).toLocaleString()}
                </td>
            </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="flex flex-wrap justify-center gap-8 mt-5 p-4 sm:p-6 lg:p-8"> {/* Added responsive padding */}
      {/* Monthly Statistics Card */}
      <div className="text-center w-full sm:w-88 md:w-96 p-6 bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col justify-between">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">This Month's Statistics 📈</h3>
        {loading ? (
          <p className="text-gray-600 text-lg my-auto">Loading data... Please wait.</p>
        ) : monthlyData && monthlyData.monthlySales > 0 ? ( // Changed condition to monthlySales > 0
          <>
            {renderChart(monthlyData)}
            {renderTable(monthlyData)}
          </>
        ) : (
          <p className="text-gray-500 italic text-lg my-auto">
            No transactions recorded this month. 🤷‍♂️
          </p>
        )}
      </div>
      {/* Image of a pie chart and a table */}

      {/* Annual Statistics Card */}
      <div className="text-center w-full sm:w-88 md:w-96 p-6 bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col justify-between">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Annual Statistics 📊</h3>
        {loading ? (
          <p className="text-gray-600 text-lg my-auto">Loading data... Please wait.</p>
        ) : annualData && annualData.annualSales > 0 ? ( // Assuming annualData also has an 'annualSales' property
          <>
            {renderChart(annualData)}
            {renderTable(annualData)}
          </>
        ) : (
          <p className="text-gray-500 italic text-lg my-auto">No annual transactions recorded. 🗓️</p>
        )}
      </div>
    </div>
  );
};

export default MoneyStatistics;